import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Crown, Check } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'

const features = [
  { text: 'Бүх ', highlight: 'premium', rest: ' сургалтанд хандах эрх' },
  { text: 'Шинэ хичээлүүд' },
  { text: 'Сургалтын материал' },
]

const EMOJI_MAP: Record<string, string> = {
  monthly: '📖',
  quarterly: '💎',
  annual: '🔥',
}

function daysToLabel(days: number): string {
  if (days >= 365) return `/ ${Math.round(days / 30)} сар`
  if (days >= 28) return `/ ${Math.round(days / 30)} сар`
  return `/ ${days} хоног`
}

interface Plan {
  id: string
  title: string
  price: number
  period: string
  emoji: string
  recommended: boolean
}

/* Decorative diagonal line SVG for cards */
function DecoLine({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 500 300"
      fill="none"
      preserveAspectRatio="none"
    >
      <line
        x1="0"
        y1="300"
        x2="500"
        y2="0"
        stroke="currentColor"
        strokeWidth="1"
      />
    </svg>
  )
}

export default function Premium() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [plans, setPlans] = useState<Plan[]>([])
  const [daysLeft, setDaysLeft] = useState(0)

  useEffect(() => {
    api.premium.getPlans()
      .then((data: any) => {
        if (Array.isArray(data) && data.length > 0) {
          setPlans(data.map((p: any, i: number) => ({
            id: p.id || p._id,
            title: p.name,
            price: p.price,
            period: daysToLabel(p.duration),
            emoji: EMOJI_MAP[p.id] || ['📖', '💎', '🔥'][i] || '📖',
            recommended: i === 1,
          })))
        }
      })
      .catch(() => {})

    if (user) {
      api.premium.getStatus()
        .then((status: any) => {
          if (status?.subscription?.endDate) {
            const end = new Date(status.subscription.endDate)
            const now = new Date()
            const diff = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
            setDaysLeft(diff)
          }
        })
        .catch(() => {})
    }
  }, [user])

  const isTeacher = user?.role === 'teacher'

  const handleSubscribe = (planId: string) => {
    if (!user) {
      navigate('/login')
      return
    }
    if (isTeacher) return
    navigate(`/payment/premium/${planId}`)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 lg:px-[245px] py-4 flex items-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 hover:text-gray-600 transition"
        >
          <ArrowLeft className="size-5" />
          Буцах
        </Link>
      </div>

      {/* Hero Section */}
      <div className="px-6 lg:px-[245px] py-12 lg:py-[72px]">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-[72px] max-w-[1430px] mx-auto">
          {/* Left — Text */}
          <div className="flex-1 flex flex-col gap-8">
            <div className="flex flex-col gap-3">
              <span className="inline-flex self-start items-center bg-accent-500/90 backdrop-blur-sm text-white text-xs font-semibold uppercase px-2.5 py-[5px] rounded-full">
                ХЯЗГААРГҮЙ ЭРХ
              </span>
              <div className="flex flex-col">
                <span className="text-5xl md:text-6xl lg:text-[72px] font-bold text-gray-800 uppercase leading-[1.1]">
                  Таны
                </span>
                <span className="text-5xl md:text-6xl lg:text-[72px] font-bold uppercase leading-[1.1] bg-gradient-to-r from-[#004da6] to-[#ff4d00] bg-clip-text text-transparent">
                  Премиум
                </span>
                <span className="text-5xl md:text-6xl lg:text-[72px] font-bold text-gray-800 uppercase leading-[1.1]">
                  Орон зай
                </span>
              </div>
            </div>
            <p className="text-lg text-gray-500 max-w-[369px]">
              Мэдлэгээ тэлж, хязгааргүй боломжуудыг нэг дороос ашиглах цаг ирлээ.
            </p>
          </div>

          {/* Right — Floating emojis + countdown */}
          <div className="relative w-[340px] h-[340px] lg:w-[489px] lg:h-[489px] shrink-0">
            {/* Floating emoji — bottom left */}
            <div className="absolute -left-8 lg:-left-[115px] bottom-0 z-10">
              <div className="bg-white border border-gray-25 rounded-[40px] shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)] p-[26px] size-[80px] lg:size-[100px] flex items-center justify-center">
                <span className="text-3xl lg:text-5xl">💎</span>
              </div>
            </div>

            {/* Floating emoji — top right */}
            <div className="absolute right-0 top-0 z-10">
              <div className="bg-white border border-gray-25 rounded-[40px] shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)] p-[26px] size-[80px] lg:size-[100px] flex items-center justify-center">
                <span className="text-3xl lg:text-5xl">📖</span>
              </div>
            </div>

            {/* Center countdown card */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:-translate-x-[calc(50%+67px)] bg-gray-900 rounded-[36px] p-9 w-[260px] lg:w-[345px] flex flex-col items-center gap-4">
              <Crown className="size-24 lg:size-[140px] text-accent-500" strokeWidth={1.5} />
              <div className="flex flex-col items-center">
                <span className="text-4xl lg:text-5xl font-bold text-white">
                  {daysLeft || 24}
                </span>
                <span className="text-sm font-bold text-white/50 uppercase">
                  Хоног үлдэв
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Teacher notice */}
      {isTeacher && (
        <div className="px-6 lg:px-[245px]">
          <div className="max-w-[1430px] mx-auto bg-primary-50 border border-primary-200 rounded-2xl px-6 py-4 text-center">
            <p className="text-sm font-semibold text-primary-700">
              Багш хэрэглэгч бүх контентэд бүрэн хандах эрхтэй тул Premium эрх авах шаардлагагүй.
            </p>
          </div>
        </div>
      )}

      {/* Pricing Section */}
      <div className="bg-white px-4 sm:px-8 lg:px-16 pt-16 lg:pt-20 pb-24 lg:pb-40">
        <div className="flex flex-col md:flex-row items-stretch justify-center gap-6 lg:gap-8 max-w-[960px] mx-auto">
          {plans.length === 0 && (
            <div className="text-gray-400 text-lg py-20">Ачаалж байна...</div>
          )}
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative overflow-hidden flex flex-col w-full ${
                plan.recommended
                  ? 'bg-gray-900 rounded-[42px] border border-gray-200 md:w-[340px] md:shrink-0'
                  : 'bg-white rounded-[42px] border border-gray-200 md:flex-1'
              }`}
            >
              {/* Decorative lines */}
              {plan.recommended ? (
                <>
                  <DecoLine className="absolute -top-[219px] right-[-80px] w-[643px] h-[643px] text-gray-700/20 rotate-[-135deg] pointer-events-none" />
                  <DecoLine className="absolute bottom-[-100px] left-[-265px] w-[560px] h-[477px] text-gray-700/20 rotate-[-122deg] pointer-events-none" />
                </>
              ) : (
                <DecoLine className="absolute -top-4 left-0 w-full h-[220px] text-gray-200 pointer-events-none" />
              )}

              {/* Orange top border for recommended */}
              {plan.recommended && (
                <div className="absolute inset-x-0 top-0 h-[6px] bg-accent-500 rounded-t-[42px]" />
              )}

              {/* Card content */}
              <div className="relative z-10 flex flex-col h-full p-5 lg:p-6">
                {/* Top: emoji + badge */}
                <div className="flex items-start justify-between mb-10 lg:mb-12">
                  <div
                    className={`rounded-[26px] p-[18px] shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)] ${
                      plan.recommended
                        ? 'bg-white/[0.08] border border-gray-100/[0.16]'
                        : 'bg-white border border-gray-100'
                    }`}
                  >
                    <span className="text-2xl">{plan.emoji}</span>
                  </div>
                  {plan.recommended && (
                    <span className="bg-accent-500 text-white text-xs font-bold uppercase px-2 py-1 rounded-full">
                      санал болгох
                    </span>
                  )}
                </div>

                {/* Title */}
                <p
                  className={`font-semibold mb-1 ${
                    plan.recommended
                      ? 'text-white text-2xl font-bold'
                      : 'text-gray-800 text-xl'
                  }`}
                >
                  {plan.title}
                </p>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-8 lg:mb-10">
                  <span
                    className={`font-bold ${
                      plan.recommended
                        ? 'text-white text-4xl'
                        : 'text-gray-800 text-2xl'
                    }`}
                  >
                    ₮{plan.price.toLocaleString()}
                  </span>
                  <span
                    className={`font-medium ${
                      plan.recommended
                        ? 'text-gray-300 text-xl'
                        : 'text-gray-400 text-lg'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>

                {/* Features */}
                <div className="flex flex-col gap-3 lg:gap-4 mb-6 flex-1">
                  {features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-1">
                      <Check
                        className={`shrink-0 text-accent-500 ${
                          plan.recommended ? 'size-6' : 'size-6'
                        }`}
                      />
                      <span
                        className={`${
                          plan.recommended
                            ? 'text-gray-300 text-lg'
                            : 'text-gray-600 text-base'
                        }`}
                      >
                        {feature.highlight ? (
                          <>
                            {feature.text}
                            <span className="text-accent-500">
                              {feature.highlight}
                            </span>
                            {feature.rest}
                          </>
                        ) : (
                          feature.text
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isTeacher}
                  className={`w-full max-w-[266px] mx-auto rounded-full font-semibold uppercase transition cursor-pointer disabled:opacity-50 ${
                    plan.recommended
                      ? 'bg-accent-500 border border-accent-500 text-white text-xl py-4 lg:py-5 hover:bg-accent-600 shadow-[0px_0px_30px_8px_rgba(255,77,0,0.24)]'
                      : 'bg-white border border-gray-200 text-gray-700 text-sm py-4 hover:bg-gray-50 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                  }`}
                >
                  Багц сонгох
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
